import { getUnixTimeInSeconds } from "@/utils/date";
import { isEmpty } from "lodash";
import Vue from "vue";

export const SERVICES_ENQUEUE_CREATION = "SERVICES_ENQUEUE_CREATION";
export const SERVICES_DE_ENQUEUE_CREATION = "SERVICES_DE_ENQUEUE_CREATION";
export const SERVICES_DELIVERING = "SERVICES_DELIVERING";
export const SERVICES_DELIVERED = "SERVICES_DELIVERED";
export const SERVICES_DELIVERY_ERROR = "SERVICES_DELIVERY_ERROR";
export const SERVICES_CREATION_ERROR = "SERVICES_CREATION_ERROR";
export const BOOKINGS_DELIVERING = "BOOKINGS_DELIVERING";
export const BOOKINGS_DELIVERED = "BOOKINGS_DELIVERED";
export const BOOKINGS_DELIVERY_ERROR = "BOOKINGS_DELIVERY_ERROR";
export const SERVICE_ACK_NOT_DELIVERED = "SERVICE_ACK_NOT_DELIVERED";
export const BOOKING_ACK_NOT_DELIVERED = "BOOKING_ACK_NOT_DELIVERED";
export const CLEAR = "CLEAR";

const initialState = () => ({
  enqueued: [],
  bookingsWatchList: {},
  waitingToBeDelivered: {},
  bookingsWaitingToBeDelivered: {},
  servicesNotDelivered: {},
  bookingsNotDelivered: {},
});

const state = initialState();

const mutations = {
  /**
   * Helpers
   * @param state
   */
  [CLEAR]: (state) => {
    Object.assign(state, initialState());
  },

  /**
   * Creation of services
   */
  [SERVICES_ENQUEUE_CREATION]: (state, { token, payload }) => {
    if (state.enqueued?.[token]) {
      const service = state.enqueued[token];
      console.info("Retrying service", { service });
      service.retries++;
      service.failed = false;
    } else {
      Vue.set(state.enqueued, token, {
        token,
        payload,
        failed: false,
        retries: 0,
        errors: [],
      });
    }
  },
  [SERVICES_DE_ENQUEUE_CREATION]: (state, { token }) => {
    Vue.delete(state.enqueued, token);
  },
  [SERVICES_CREATION_ERROR]: (state, { event: { token }, error }) => {
    const updated = state.enqueued[token];
    updated.failed = true;
    updated.errors.push(error);
    Vue.set(state.enqueued, token, updated);
  },

  /**
   * Check if services are being delivered
   */
  [SERVICES_DELIVERING]: (state, { token, payload }) => {
    if (!state.waitingToBeDelivered[token]) {
      Vue.set(state.waitingToBeDelivered, token, {
        token,
        payload,
        inserted: getUnixTimeInSeconds(),
      });
    }
  },
  [SERVICES_DELIVERED]: (state, payload) => {
    payload
      .map((s) => s.service.xReqCode)
      .forEach((token) => Vue.delete(state.waitingToBeDelivered, token));
  },
  [SERVICES_DELIVERY_ERROR]: (state, { token, payload }) => {
    const { inserted } = state.waitingToBeDelivered[token];
    Vue.delete(state.waitingToBeDelivered, token);
    Vue.set(state.servicesNotDelivered, token, { token, payload, inserted });
  },
  [SERVICE_ACK_NOT_DELIVERED]: (state, { token }) => {
    Vue.delete(state.servicesNotDelivered, token);
  },

  /**
   * Check if bookings are being delivered
   */
  [BOOKINGS_DELIVERING]: (state, payload) => {
    if (!isEmpty(payload)) {
      payload.forEach((booking) => {
        if (state.bookingsWatchList?.[booking.xReqCode]) {
          const booking = state.bookingsWatchList[booking.xReqCode];
          Vue.set(state.bookingsWaitingToBeDelivered, booking.xReqCode, {
            payload: booking,
            token: booking.xReqCode,
            inserted: getUnixTimeInSeconds(),
          });
        }
      });
    }
    Vue.set(state, "bookingsWatchList", payload);
  },
  [BOOKINGS_DELIVERED]: (state, payload) => {
    payload
      .map((s) => s.service.xReqCode)
      .forEach((token) => {
        if (state.bookingsWaitingToBeDelivered[token]) {
          Vue.delete(state.bookingsWaitingToBeDelivered, token);
        }
      });
  },
  [BOOKINGS_DELIVERY_ERROR]: (state, { token, payload }) => {
    const { inserted } = state.bookingsWaitingToBeDelivered[token];
    Vue.delete(state.bookingsWaitingToBeDelivered, token);
    Vue.set(state.bookingsNotDelivered, token, {
      token,
      payload,
      inserted,
    });
  },
  [BOOKING_ACK_NOT_DELIVERED]: (state, { token }) => {
    Vue.delete(state.bookingsNotDelivered, token);
  },
};

const actions = {
  addEnqueued({ commit }, { token, payload }) {
    commit(SERVICES_ENQUEUE_CREATION, { token, payload });
  },
  removeEnqueued({ commit }, { token, payload }) {
    commit(SERVICES_DE_ENQUEUE_CREATION, { token, payload });
  },
  addWaitingToBeDelivered({ commit }, { token, payload }) {
    commit(SERVICES_DELIVERING, { token, payload });
  },
  removeToBeDelivered({ commit }, services) {
    commit(SERVICES_DELIVERED, services);
  },
  addServiceDeliveryError({ commit }, { token, payload }) {
    commit(SERVICES_DELIVERY_ERROR, { token, payload });
  },
  addError({ commit }, { event, error }) {
    commit(SERVICES_CREATION_ERROR, { event, error });
  },
  addBookingsToBeDelivered({ commit }, bookings) {
    commit(BOOKINGS_DELIVERING, bookings);
  },
  removeBookingsToBeDelivered({ commit }, bookings) {
    commit(BOOKINGS_DELIVERED, bookings);
  },
  addBookingDeliveryError({ commit }, { token, payload }) {
    commit(BOOKINGS_DELIVERY_ERROR, { token, payload });
  },
  removeNotDeliveredService({ commit }, { token }) {
    commit(SERVICE_ACK_NOT_DELIVERED, { token });
  },
  removeNotDeliveredBooking({ commit }, { token }) {
    commit(BOOKING_ACK_NOT_DELIVERED, { token });
  },
  clear({ commit }) {
    commit(CLEAR);
  },
};

const getters = {
  servicesWithError(state) {
    return Object.fromEntries(
      Object.entries(state.enqueued)

        .filter(([key, value]) => value.failed)
    );
  },
  servicesWaitingToBeDelivered(state) {
    return state.waitingToBeDelivered;
  },
  bookingsWaitingToBeDelivered(state) {
    return state.bookingsWaitingToBeDelivered;
  },
  servicesNotDelivered(state) {
    return state.servicesNotDelivered;
  },
  bookingsNotDelivered(state) {
    return state.bookingsNotDelivered;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
